<template>
    <div v-if="model">
        <rkm-area
            no-margin-top
            title="Filtros"
            type="fieldset"
        >
            <rkm-form
                ref="Form"
                show-legend
            >
                <rkm-row>
                    <rkm-col cols="2">
                        <rkm-form-select
                            :options="form.competencias"
                            :validations="validations.competencia"
                            @change="handleCompetenciaChange"
                            label="Competência"
                            show-default-option
                            text-field="valor"
                            v-model="model.competenciaId"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="4">
                        <rkm-form-select
                            :options="form.tiposInconsistencia"
                            :validations="validations.tipoInconsistencia"
                            @change="handleTipoInconsistenciaChange"
                            label="Tipo de inconsistência"
                            show-default-option
                            text-field="descricao"
                            v-model="model.tipoInconsistencia"
                            value-field="valor"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.unidadesExecutantes"
                            @change="handleUnidadeExecutanteChange"
                            default-option-text="Todas"
                            label="Unidade executante"
                            show-default-option
                            text-field="nomeFantasia"
                            v-model="model.unidadeExecutanteId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :disabled="!model.tipoInconsistencia"
                            :repository="repositories.relatorioInconsistencia.usuarioExecutante"
                            :request-query-string-params="{
                                competencia_id: model.competenciaId,
                                tipo_inconsistencia: model.tipoInconsistencia,
                                unidade_executante_id: model.unidadeExecutanteId
                            }"
                            :search-fields="[{ key: 'cadastro_cidadao.nome', searchable: true }]"
                            @select="() => handleUsuarioExecutanteChange(model.usuarioExecutante.id)"
                            label="Profissional executante"
                            text-field="nome"
                            v-model="model.usuarioExecutante"
                        />
                    </rkm-col>

                    <rkm-col cols="6">
                        <rkm-form-select
                            :options="form.ocupacoes"
                            @change="handleOcupacaoChange"
                            default-option-text="Todas"
                            label="CBO"
                            show-default-option
                            text-field="descricao"
                            v-model="model.ocupacaoId"
                            value-field="id"
                        />
                    </rkm-col>
                </rkm-row>

                <rkm-row>
                    <rkm-col cols="6">
                        <rkm-form-input-autocomplete
                            :disabled="!model.tipoInconsistencia"
                            :repository="repositories.relatorioInconsistencia.procedimento"
                            :request-query-string-params="{
                                competencia_id: model.competenciaId,
                                tipo_inconsistencia: model.tipoInconsistencia,
                                unidade_executante_id: model.unidadeExecutanteId,
                                usuario_executante_id: model.usuarioExecutante ? model.usuarioExecutante.id : null,
                                ocupacao_id: model.ocupacaoId
                            }"
                            :search-fields="[
                                { key: 'procedimento.codigo', searchable: true },
                                { key: 'procedimento.nome', searchable: true },
                            ]"
                            :text-field="p => `${formatProcedimento(p.codigo)} - ${p.nome}`"
                            @select="() => handleProcedimentoChange(model.procedimento.id)"
                            label="Procedimento"
                            v-model="model.procedimento"
                        />
                    </rkm-col>

                    <rkm-col>
                        <rkm-form-multiselect
                            :options="form.cids"
                            :select-limit="null"
                            @update:dropdownClosed="handleCidChange"
                            label="CID(s)"
                            option-name-key="descricao"
                            option-text-selecteds="cid selecionado"
                            v-model="model.cids"
                            value-field="id"
                        />
                    </rkm-col>

                    <rkm-col cols="auto">
                        <rkm-button
                            :label="false"
                            @click="$refs.Form.validate(search)"
                            color="primary"
                        >
                            Buscar
                        </rkm-button>

                        <rkm-button
                            :label="false"
                            @click="reset"
                        >
                            Limpar
                        </rkm-button>
                    </rkm-col>
                </rkm-row>
            </rkm-form>
        </rkm-area>

        <template v-if="data">
            <rkm-area
                no-margin-top
                title="Resultado"
            >
                <template v-slot:trailing>
                    <rkm-button
                        @click="print"
                        color="info"
                        small
                        title="Imprimir"
                    >
                        <rkm-icon icon="print" />
                    </rkm-button>

                    <rkm-button
                        @click="exportTo"
                        color="info"
                        small
                        title="Exportar"
                    >
                        <rkm-icon icon="file-export" />
                    </rkm-button>
                </template>

                <rkm-table
                    :fields="fields"
                    :items="data.itens"
                    :sort-by="sortBy"
                    :sort-desc="sortDesc"
                    @sort-changed="onSortChanged"
                    class="table-resultado"
                >
                    <template v-slot:cell(inconsistencias)="{item}">
                        <div
                            v-for="(inconsistencia, index) in item.inconsistencias"
                            :key="uniqueId(`inconsistencia-${index}`)"
                        >
                            {{inconsistencia.inconsistenciaDescricao}}
                        </div>
                    </template>

                    <template v-slot:afterTable>
                        <div class="table-resultado-header-footer text-center">
                            <span>
                                <strong>Total:</strong> {{ data.itens.length }}
                            </span>
                        </div>
                    </template>
                </rkm-table>
            </rkm-area>
        </template>
    </div>
</template>

<script>
// Mixins.
import {error, file, formats, repository, stringCase} from '@/mixins'

// Others.
import moment from 'moment'

// Vuex.
import {mapActions} from 'vuex'

export default {
    computed: {
        validations() {
            return {
                competencia: {required: true},
                tipoInconsistencia: {required: true},
            }
        },
        fields() {
            return [
                { key: 'dataProcessamento', label: 'Data de processamento', formatter: v => this.formatDate(v), sortable: true },
                { key: 'prontuario', label: 'Prontuário', sortable: true },
                { key: 'pacienteNome', label: 'Paciente', sortable: true },
                { key: 'unidadeExecutanteNomeFantasia', label: 'Unidade executante', sortable: true },
                { key: 'usuarioExecutanteNome', label: 'Profissional executante', sortable: true },
                { key: 'ocupacaoCodigo', label: 'CBO', sortable: true },
                { key: 'procedimentoDescricao', label: 'Procedimento', sortable: true },
                { key: 'cidDescricao', label: 'CID', sortable: true },
                { key: 'inconsistencias', label: 'Inconsistências' },
            ]
        },
    },
    created() {
        this.repositories = {
            exportacao: this.getConectaSusRepository('exportacao.apac'),
            impressao: this.getConectaSusRepository('impressao.apac'),
            relatorioInconsistencia: this.getConectaSusRepository('apac.relatorioInconsistencia'),
        }

        this.load()
    },
    data: () => ({
        data: null,
        form: null,
        model: null,
        others: {moment},
        sortBy: 'dataProcessamento',
        sortDesc: false,
        unwatchers: {
            usuarioExecutante: null,
            procedimento: null,
        }
    }),
    methods: {
        ...mapActions(['setLoading', 'unsetLoading']),
        load() {
            this.setLoading()

            this.repositories.relatorioInconsistencia.create()
                .then((response) => {
                    this.form = response.data.form
                    this.model = response.data.model

                    this.addWatchers()
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        addWatchers() {
            this.unwatchers.usuarioExecutante = this.$watch('model.usuarioExecutante', value => {

                if (!value) {

                    this.handleUsuarioExecutanteChange(null)
                }
            })

            this.unwatchers.procedimento = this.$watch('model.procedimento', value => {

                if (!value) {

                    this.handleProcedimentoChange(null)
                }
            })
        },
        removeWatchers() {
            this.unwatchers.usuarioExecutante()
            this.unwatchers.procedimento()
        },
        search() {
            const request = {
                competenciaId: this.model.competenciaId,
                tipoInconsistencia: this.model.tipoInconsistencia,
                unidadeExecutanteId: this.model.unidadeExecutanteId,
                usuarioExecutanteId: this.model.usuarioExecutante ? this.model.usuarioExecutante.id : null,
                ocupacaoId: this.model.ocupacaoId,
                procedimentoId: this.model.procedimento ? this.model.procedimento.id : null,
                cids: this.model.cids
            }

            this.setLoading()

            this.repositories.relatorioInconsistencia.index(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => this.data = Object.assign(response.data))
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        reset() {
            this.removeWatchers()

            this.form.tiposInconsistencia = []
            this.form.unidadesExecutantes = []
            this.form.ocupacoes = []
            this.form.cids = []

            this.model.competenciaId = null
            this.model.tipoInconsistencia = null
            this.model.unidadeExecutanteId = null
            this.model.usuarioExecutante = null
            this.model.ocupacaoId = null
            this.model.procedimento = null
            this.model.cids = []

            this.addWatchers()

            this.data = null

            this.$nextTick().then(() => this.$refs.Form.resetValidator())
        },
        print() {
            this.setLoading()

            const request = {
                competenciaId: this.model.competenciaId,
                tipoInconsistencia: this.model.tipoInconsistencia,
                unidadeExecutanteId: this.model.unidadeExecutanteId,
                usuarioExecutanteId: this.model.usuarioExecutante ? this.model.usuarioExecutante.id : null,
                ocupacaoId: this.model.ocupacaoId,
                procedimentoId: this.model.procedimento ? this.model.procedimento.id : null,
                cids: this.model.cids,
                sortBy: this.sortBy ? this.camelToSnake(this.sortBy) : null,
                sortDesc: this.sortDesc,
            }

            this.repositories.impressao.relatorioInconsistencia(this.camelObjectKeysToSnakeRecursive(request))
                .then(({data, headers}) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        exportTo() {
            this.setLoading()

            const request = {
                competenciaId: this.model.competenciaId,
                tipoInconsistencia: this.model.tipoInconsistencia,
                unidadeExecutanteId: this.model.unidadeExecutanteId,
                usuarioExecutanteId: this.model.usuarioExecutante ? this.model.usuarioExecutante.id : null,
                ocupacaoId: this.model.ocupacaoId,
                procedimentoId: this.model.procedimento ? this.model.procedimento.id : null,
                cids: this.model.cids,
                sortBy: this.sortBy ? this.camelToSnake(this.sortBy) : null,
                sortDesc: this.sortDesc,
            }

            this.repositories.exportacao.relatorioInconsistencia(this.camelObjectKeysToSnakeRecursive(request))
                .then(({data, headers}) => this.openFile(data, headers['content-type']))
                .finally(() => this.unsetLoading())
        },
        handleCompetenciaChange(value) {
            this.removeWatchers()

            this.form.tiposInconsistencia = []
            this.form.unidadesExecutantes = []
            this.form.ocupacoes = []
            this.form.cids = []
            this.model.tipoInconsistencia = null
            this.model.unidadeExecutanteId = null
            this.model.usuarioExecutante = null
            this.model.ocupacaoId = null
            this.model.procedimento = null
            this.model.cids = []

            this.addWatchers()

            this.data = null

            if (value === null) {
                return
            }

            const request = {
                listar: 'tipos-inconsistencia',
                competenciaId: value,
            }

            this.setLoading()

            this.repositories.relatorioInconsistencia.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.tiposInconsistencia = Object.assign(response.data.tiposInconsistencia)
                    this.form.unidadesExecutantes = Object.assign(response.data.unidadesExecutantes)
                    this.form.ocupacoes = Object.assign(response.data.ocupacoes)
                    this.form.cids = Object.assign(response.data.cids)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleTipoInconsistenciaChange(value) {
            this.removeWatchers()

            this.form.unidadesExecutantes = []
            this.form.ocupacoes = []
            this.form.cids = []
            this.model.unidadeExecutanteId = null
            this.model.usuarioExecutante = null
            this.model.ocupacaoId = null
            this.model.procedimento = null
            this.model.cids = []

            this.addWatchers()

            this.data = null

            if (value === null) {
                return
            }

            const request = {
                listar: 'unidades-executantes',
                competenciaId: this.model.competenciaId,
                tipoInconsistencia: value
            }

            this.setLoading()

            this.repositories.relatorioInconsistencia.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.unidadesExecutantes = Object.assign(response.data.unidadesExecutantes)
                    this.form.ocupacoes = Object.assign(response.data.ocupacoes)
                    this.form.cids = Object.assign(response.data.cids)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUnidadeExecutanteChange(value) {
            this.removeWatchers()

            this.form.ocupacoes = []
            this.form.cids = []
            this.model.usuarioExecutante = null
            this.model.ocupacaoId = null
            this.model.procedimento = null
            this.model.cids = []

            this.addWatchers()

            this.data = null

            const request = {
                listar: 'ocupacoes',
                competenciaId: this.model.competenciaId,
                tipoInconsistencia: this.model.tipoInconsistencia,
                unidadeExecutanteId: value,
            }

            this.setLoading()

            this.repositories.relatorioInconsistencia.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.ocupacoes = Object.assign(response.data.ocupacoes)
                    this.form.cids = Object.assign(response.data.cids)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleUsuarioExecutanteChange(value) {
            this.removeWatchers()

            this.form.ocupacoes = []
            this.form.cids = []
            this.model.ocupacaoId = null
            this.model.procedimento = null
            this.model.cids = []

            this.addWatchers()

            this.data = null

            const request = {
                listar: 'ocupacoes',
                competenciaId: this.model.competenciaId,
                tipoInconsistencia: this.model.tipoInconsistencia,
                unidadeExecutanteId: this.model.unidadeExecutanteId,
                usuarioExecutanteId: value
            }

            this.setLoading()

            this.repositories.relatorioInconsistencia.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.ocupacoes = Object.assign(response.data.ocupacoes)
                    this.form.cids = Object.assign(response.data.cids)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleOcupacaoChange(value) {
            this.removeWatchers()

            this.form.cids = []
            this.model.procedimento = null
            this.model.cids = []

            this.addWatchers()

            this.data = null

            const request = {
                listar: 'cids',
                competenciaId: this.model.competenciaId,
                tipoInconsistencia: this.model.tipoInconsistencia,
                unidadeExecutanteId: this.model.unidadeExecutanteId,
                usuarioExecutanteId: this.model.usuarioExecutante ? this.model.usuarioExecutante.id : null,
                ocupacaoId: value
            }

            this.setLoading()

            this.repositories.relatorioInconsistencia.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.cids = Object.assign(response.data.cids)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleProcedimentoChange(value) {
            this.removeWatchers()

            this.form.cids = []
            this.model.cids = []

            this.addWatchers()

            this.data = null

            const request = {
                listar: 'cids',
                competenciaId: this.model.competenciaId,
                tipoInconsistencia: this.model.tipoInconsistencia,
                unidadeExecutanteId: this.model.unidadeExecutanteId,
                usuarioExecutanteId: this.model.usuarioExecutante ? this.model.usuarioExecutante.id : null,
                ocupacaoId: this.model.ocupacaoId,
                procedimentoId: value
            }

            this.setLoading()

            this.repositories.relatorioInconsistencia.list(this.camelObjectKeysToSnakeRecursive(request))
                .then(response => {
                    this.form.cids = Object.assign(response.data.cids)
                })
                .catch(this.handleError)
                .finally(() => this.unsetLoading())
        },
        handleCidChange() {
            this.data = null
        },
        onSortChanged(context) {
            this.sortBy = context.sortBy
            this.sortDesc = context.sortDesc
        },
    },
    mixins: [error, file, formats, repository, stringCase],
}
</script>

<style scoped>
.table-resultado >>> .table-resultado-header-footer {
    background: #f1f1f1;
    border: 1px solid #dee2e6;
    color: var(--gray-dark);
    font-weight: 600;
    padding: 0.5rem;
}
</style>